/**
 * RIDAA Product Form & Sticky Add-to-Cart
 * Handles Variant selection, Live Price updates, Media sync, and Sticky cart synchronization
 */

class RidaaProductForm {
  constructor() {
    this.container = document.querySelector('[data-product-container]');
    if (!this.container) return;

    this.productData = this.getProductData();
    if (!this.productData) return;

    this.idInput = this.container.querySelector('input[name="id"]');
    this.priceContainer = this.container.querySelector('[data-product-price]');
    this.submitBtn = this.container.querySelector('[type="submit"]');
    this.stickyBar = document.querySelector('[data-sticky-cart-bar]');
    this.stickyBtn = this.stickyBar ? this.stickyBar.querySelector('[type="submit"]') : null;
    this.stickyPrice = this.stickyBar ? this.stickyBar.querySelector('[data-sticky-price]') : null;

    this.initVariantPicker();
    this.initStickyCart();
  }

  getProductData() {
    const script = this.container.querySelector('[data-product-json]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      console.error('Failed to parse product JSON', e);
      return null;
    }
  }

  initVariantPicker() {
    // Listen for option clicks (swatches / buttons)
    this.container.querySelectorAll('[data-option-value]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const optionIndex = parseInt(btn.dataset.optionIndex, 10);
        const value = btn.dataset.optionValue;

        // Deselect sibling options
        const parent = btn.closest('.swatch-group, .option-buttons-group');
        if (parent) {
          parent.querySelectorAll('[data-option-value]').forEach((sibling) => {
            sibling.classList.remove('is-active');
            sibling.setAttribute('aria-checked', 'false');
          });
        }

        btn.classList.add('is-active');
        btn.setAttribute('aria-checked', 'true');

        // Update selected text label
        const labelSelected = this.container.querySelector(`[data-option-selected="${optionIndex}"]`);
        if (labelSelected) labelSelected.textContent = value;

        this.onOptionChanged();
      });
    });

    // Listen for select dropdown changes
    this.container.querySelectorAll('[data-option-select]').forEach((select) => {
      select.addEventListener('change', () => {
        this.onOptionChanged();
      });
    });
  }

  getCurrentSelectedOptions() {
    const selected = [];
    // Collect active swatch/button values
    for (let i = 0; i < 3; i++) {
      const activeBtn = this.container.querySelector(`[data-option-index="${i}"].is-active`);
      if (activeBtn) {
        selected.push(activeBtn.dataset.optionValue);
      } else {
        const select = this.container.querySelector(`select[data-option-index="${i}"]`);
        if (select) {
          selected.push(select.value);
        }
      }
    }
    return selected;
  }

  onOptionChanged() {
    const selectedOptions = this.getCurrentSelectedOptions();
    if (!selectedOptions.length) return;

    // Find matching variant
    const matchedVariant = this.productData.variants.find((variant) => {
      return variant.options.every((optValue, idx) => optValue === selectedOptions[idx]);
    });

    this.updateVariantState(matchedVariant);
  }

  updateVariantState(variant) {
    if (!variant) {
      if (this.submitBtn) {
        this.submitBtn.disabled = true;
        const txt = this.submitBtn.querySelector('.btn__text') || this.submitBtn;
        txt.textContent = window.RidaaStrings?.unavailable || 'Unavailable';
      }
      if (this.stickyBtn) {
        this.stickyBtn.disabled = true;
        const stickyTxt = this.stickyBtn.querySelector('.btn__text') || this.stickyBtn;
        stickyTxt.textContent = window.RidaaStrings?.unavailable || 'Unavailable';
      }
      return;
    }

    // Update variant ID
    if (this.idInput) {
      this.idInput.value = variant.id;
    }

    // Update URL
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set('variant', variant.id);
    window.history.replaceState({ path: newUrl.href }, '', newUrl.href);

    // Update prices
    const formattedPrice = this.formatMoney(variant.price);
    if (this.priceContainer) {
      let priceHtml = `<span>${formattedPrice}</span>`;
      if (variant.compare_at_price > variant.price) {
        priceHtml += ` <span class="product-card__price--compare">${this.formatMoney(variant.compare_at_price)}</span>`;
      }
      this.priceContainer.innerHTML = priceHtml;
    }
    if (this.stickyPrice) {
      this.stickyPrice.textContent = formattedPrice;
    }

    // Update availability
    if (variant.available) {
      if (this.submitBtn) {
        this.submitBtn.disabled = false;
        const txt = this.submitBtn.querySelector('.btn__text') || this.submitBtn;
        txt.textContent = window.RidaaStrings?.addToCart || 'Add to Bag';
      }
      if (this.stickyBtn) {
        this.stickyBtn.disabled = false;
        const stickyTxt = this.stickyBtn.querySelector('.btn__text') || this.stickyBtn;
        stickyTxt.textContent = window.RidaaStrings?.addToCart || 'Add to Bag';
      }
    } else {
      if (this.submitBtn) {
        this.submitBtn.disabled = true;
        const txt = this.submitBtn.querySelector('.btn__text') || this.submitBtn;
        txt.textContent = window.RidaaStrings?.soldOut || 'Sold Out';
      }
      if (this.stickyBtn) {
        this.stickyBtn.disabled = true;
        const stickyTxt = this.stickyBtn.querySelector('.btn__text') || this.stickyBtn;
        stickyTxt.textContent = window.RidaaStrings?.soldOut || 'Sold Out';
      }
    }

    // Media sync if variant has featured media
    const mediaId = (variant.featured_media && variant.featured_media.id) || (variant.featured_image && variant.featured_image.id);
    if (mediaId) {
      const targetThumb = document.querySelector(`[data-media-id="${mediaId}"]`);
      if (targetThumb) targetThumb.click();
    }
  }

  initStickyCart() {
    if (!this.stickyBar || !this.submitBtn) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        // When main submit button is NOT visible, show sticky bar
        if (!entry.isIntersecting && window.scrollY > 300) {
          this.stickyBar.classList.add('is-visible');
        } else {
          this.stickyBar.classList.remove('is-visible');
        }
      });
    }, { rootMargin: '0px 0px -50px 0px' });

    observer.observe(this.submitBtn);

    // Sticky Add to Cart form submit
    const stickyForm = this.stickyBar.querySelector('form');
    if (stickyForm) {
      stickyForm.addEventListener('submit', (e) => {
        e.preventDefault();
        const mainForm = this.container.querySelector('form[action*="/cart/add"]');
        if (mainForm && window.Ridaa?.cartDrawer) {
          window.Ridaa.cartDrawer.handleAddToCart(mainForm);
        }
      });
    }
  }

  formatMoney(cents) {
    if (window.Shopify && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    const amount = (cents / 100).toFixed(2);
    const currency = window.Shopify?.currency?.active || 'MAD';
    return `${amount} ${currency}`;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.Ridaa = window.Ridaa || {};
  window.Ridaa.productForm = new RidaaProductForm();
});
